/**
 * Booking Dashboard Page Component
 * /admin/bookings/dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import apiClient from '../api/client';

const BookingDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [todayBookings, setTodayBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [kpis, setKPIs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [summaryData, todayData, upcomingData, revenueData, kpisData] = await Promise.all([
        apiClient.getBookingDashboardSummary().catch(() => null),
        apiClient.getTodayBookings().catch(() => ({ bookings: [] })),
        apiClient.getUpcomingBookings(7).catch(() => ({ bookings: [] })),
        apiClient.getRevenueMetrics().catch(() => null),
        apiClient.getKPIs().catch(() => null),
      ]);

      setSummary(summaryData);
      setTodayBookings(todayData?.bookings || []);
      setUpcomingBookings(upcomingData?.bookings || []);
      setRevenue(revenueData);
      setKPIs(kpisData);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'info',
      completed: 'success',
      cancelled: 'error',
      in_progress: 'primary',
    };
    return colors[status] || 'default';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'AED 0.00';
    return `AED ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" component="h1" gutterBottom>
        Booking Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" component="div">
                  {summary.total_bookings || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Bookings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" component="div" color="info.main">
                  {summary.today_bookings || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Today's Bookings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" component="div" color="warning.main">
                  {summary.pending_bookings || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" component="div" color="success.main">
                  {summary.completed_bookings || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Revenue Metrics */}
      {revenue && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div" color="primary.main">
                  {formatCurrency(revenue.total_revenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div" color="success.main">
                  {formatCurrency(revenue.today_revenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Today's Revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div" color="info.main">
                  {formatCurrency(revenue.month_revenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This Month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Today's Bookings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Today's Bookings ({todayBookings.length})
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Service Type</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {todayBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No bookings for today
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                todayBookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>{booking.booking_number || booking.id.substring(0, 8)}</TableCell>
                    <TableCell>{booking.customer_name || booking.customer_email || '-'}</TableCell>
                    <TableCell>{booking.service_type || '-'}</TableCell>
                    <TableCell>{formatDateTime(booking.start_datetime)}</TableCell>
                    <TableCell>
                      <Chip
                        label={booking.status || 'pending'}
                        color={getStatusColor(booking.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Upcoming Bookings */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upcoming Bookings (Next 7 Days) ({upcomingBookings.length})
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Service Type</TableCell>
                <TableCell>Start Date/Time</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {upcomingBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No upcoming bookings
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                upcomingBookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>{booking.booking_number || booking.id.substring(0, 8)}</TableCell>
                    <TableCell>{booking.customer_name || booking.customer_email || '-'}</TableCell>
                    <TableCell>{booking.service_type || '-'}</TableCell>
                    <TableCell>{formatDateTime(booking.start_datetime)}</TableCell>
                    <TableCell>
                      <Chip
                        label={booking.status || 'pending'}
                        color={getStatusColor(booking.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default BookingDashboard;

